import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useTourPrices } from '../hooks/api/tour-prices';
import { IOnlineTourPriceResult } from '../utils/types';

type OnlineTourDetailPricesProps = {
  tourId: number;
};

export default function OnlineTourDetailPrices({
  tourId,
}: OnlineTourDetailPricesProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useTourPrices({
    tourId,
  });

  const ageGroups: {
    label: string;
    bedStatus: string;
  }[] = [];
  const bedTypes: string[] = [];
  const groupedPrices: {
    [ageGroup: string]: { [bedType: string]: IOnlineTourPriceResult };
  } = {};

  Array.from(data || [])
    .sort((a, b) => a.id - b.id)
    .forEach((result) => {
      const bedType = result.bedType;
      const ageGroup = `${result.ageGroup}/${result.bedStatus}`;

      if (
        !ageGroups.find(
          (ag) =>
            ag.label === result.ageGroup && ag.bedStatus === result.bedStatus,
        )
      ) {
        ageGroups.push({
          label: result.ageGroup,
          bedStatus: result.bedStatus,
        });
      }
      if (!bedTypes.includes(bedType)) {
        bedTypes.push(bedType);
      }

      if (!groupedPrices[ageGroup]) {
        groupedPrices[ageGroup] = {};
      }

      groupedPrices[ageGroup][bedType] = result;
    });

  ageGroups.sort((a, b) =>
    a.label.localeCompare(b.label, 'tr', { sensitivity: 'base' }),
  );

  const cellWidth = `${100 / (ageGroups.length + 1)}%`;

  return (
    <Box>
      {isLoading && <LinearProgress />}
      {!isLoading && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (t) => t.palette.secondary.dark,
                  color: 'white',
                }}
              >
                <TableCell>
                  <Typography color="white">
                    {t('onlineTourDetailPrices.bedType')}
                  </Typography>
                </TableCell>
                {ageGroups.map((age, i) => (
                  <TableCell key={`${age.label}-${age.bedStatus}`}>
                    <Typography color="white" variant="inherit">
                      {age.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bedTypes.map((bedType) => (
                <TableRow key={bedType}>
                  <TableCell width={cellWidth}>{bedType}</TableCell>
                  {ageGroups.map((ageGroup) => {
                    const ageKey = `${ageGroup.label}/${ageGroup.bedStatus}`;
                    const result =
                      groupedPrices[ageKey]?.[bedType] || undefined;
                    return (
                      <TableCell
                        key={`prc-${bedType}-${ageKey}`}
                        width={cellWidth}
                      >
                        {result ? (
                          <>
                            <Price
                              color="primary"
                              price={{
                                price: result.price,
                                currencyCode: result.currencyCode,
                              }}
                            />
                            <Typography
                              color="textSecondary"
                              variant="caption"
                              display="block"
                            >
                              {result.bedStatus &&
                                (result.bedStatus !== 'Yataklı'
                                  ? result.bedStatus
                                  : '')}
                            </Typography>
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function Price(props: {
  price?: { price: number; currencyCode: string };
  color: string;
}) {
  return (
    <Typography color={props.color} fontWeight={500}>
      {props.price
        ? props.price.price.toLocaleString('tr-TR', {
            style: 'currency',
            currency: props.price.currencyCode,
            maximumFractionDigits: 0,
          })
        : '-'}
    </Typography>
  );
}
